// Input router (IMPLEMENTATION §2.7, §2.8, UX §3): turns the pointer FSM's gestures (gestureFsm.ts via the
// DOM glue in PointerController.ts) into Commands, FX and UI intents. It is the only input code that reads
// the sim — through sim/query.ts, never sim/rules or sim/systems — and it dispatches through the
// GameController (dispatch-time apply). Everything visual goes through injected sinks (DOM ghost, bubbles,
// the renderer's input overlay, the UI store), so Node tests drive it with fakes.
//   down        → the §2.7 IDLE table for the current mode (hitTest): deposit / tapOnly / paint / erase / press
//   tap         → cold open: wiggle the Gallery; else tap-select then tap-target (selection.ts, the same
//                 resolver as a drop) or the object's own tap semantics (cards, the Build tools)
//   drag        → grab (board payloads), DOM ghost at the lifted hotspot, valid-target glows (query.dropTargets),
//                 hysteresis target, preview bubble (query.resolveDrop), sheet collapse, toasts fade;
//                 drop → `drop` → commit, or snap back 180 ms + reason bubble 1.5 s + ui:bonk ≤ 1 per 1.5 s
//                 (no bubble for `cooldown`); outside everything / cancel → `release` + silent snap back
//   paint/erase → Build chute strokes (paint.ts path, query.paintPreview: unaffordable tail red, not placed)
//   telemetry   → THE source of `drag` rows (one per drag) and `rage_tap` rows (every board tap feeds the
//                 M12 detector, debug/rageTap.ts) in the M12 row format (debug/telemetryRows.ts; M12 decision 4:
//                 result = the resolver outcome, `snapback` (+ reason: the refusal, offBoard) or `cancel`)
import type { CopyData, UiData } from '../data/schema.ts';
import { createRageTapDetector, type RageTapDetector } from '../debug/rageTap.ts';
import type { TelemetryRow } from '../debug/telemetry.ts';
import { dragRow, rageTapRow } from '../debug/telemetryRows.ts';
import type { IconKey } from '../fx/icons.ts';
import { type Cell, cellCenter, type Layout } from '../layout/computeLayout.ts';
import type { InputGlow, InputOverlay } from '../render/Renderer.ts';
import type { CmdResult, Command, Dst, RejectReason, Src } from '../sim/commands.ts';
import type { Dir } from '../sim/core/types.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { BType, FamId, OreId, ShopId } from '../sim/gen/ids.ts';
import {
  canUndoSell,
  cellView,
  type DropOutcome,
  dropTargets,
  handAccepts,
  hasFeature,
  paintPreview,
  resolveDrop,
  tutorialView,
} from '../sim/query.ts';
import type { Building, ItemRef, SimState } from '../sim/state.ts';
import { fillCopy } from '../ui/copy.ts';
import type { DropTarget, DropZoneRegistry, ZoneId } from './dropZones.ts';
import type { CancelReason, DownKind, GestureOut, GestureStateKind } from './gestureFsm.ts';
import { type HitObj, hasLongPress, hitCell, hitTest, isDraggable, objKey } from './hitTest.ts';
import { stickyCell } from './hysteresis.ts';
import type { KeyAction } from './keys.ts';
import {
  effectiveMode,
  effectiveTool,
  MODE_START,
  type Mode,
  type ModeCaps,
  type ModeState,
  modeCaps,
  setBuild,
  setTool,
  type Tool,
} from './modes.ts';
import type { GestureMeta } from './PointerController.ts';
import {
  type CellClass,
  endsOnSink,
  extendPath,
  lastDir,
  type PaintPath,
  startPath,
} from './paint.ts';
import { glowOf, type Names, namesOf, previewText, reasonText, type TextCtx } from './preview.ts';
import { type Selection, selectionTap, type TapTarget } from './selection.ts';

// ---- sinks ------------------------------------------------------------------------------------------------

export interface GhostFx {
  show(key: IconKey, sizePx: number): void;
  moveTo(cx: number, cy: number): void;
  /** Animates back to (x, y) over `ms` (Back.out), then hides; 0 ms hides at once. */
  snapBack(x: number, y: number, ms: number): void;
  hide(): void;
  visible(): boolean;
}

export interface BubbleFx {
  /** The preview bubble above the lifted item (an icon before text[at]); null text hides it. */
  preview(
    text: string | null,
    bad: boolean,
    x: number,
    y: number,
    icon?: { key: IconKey; at: number } | null,
  ): void;
  /** The red reason bubble after a snap-back (stays `ms`). */
  reason(text: string, x: number, y: number, ms: number): void;
}

export interface OverlaySink {
  setInputOverlay(o: InputOverlay): void;
  setHighlight(cell: Cell | null): void;
}

export interface ModeView {
  mode: Mode;
  tool: Tool;
  canBuild: boolean;
  canChute: boolean;
  /** Build-undo entries (the Undo n button). */
  undo: number;
}

export interface SelectionView {
  k: 'item' | 'shop';
  id: number | null;
  item: ShopId | null;
  /** Counter "Tap to sell +$X" (null = the counter would refuse it). */
  sellValue: number | null;
}

/** UI intents the M7 shell renders (cards, toasts, hints); the M6 spike chrome shows a status line. */
export type UiIntent =
  | { k: 'info'; target: HitObj }
  | { k: 'card'; target: HitObj; mode: Mode }
  | { k: 'undoToast'; token: number; value: number }
  | { k: 'rotateHandle'; bid: number; ms: number }
  | { k: 'hint'; text: string }
  | { k: 'nudge' }
  | { k: 'status'; text: string }
  | { k: 'shop'; open: boolean | 'toggle' };

export interface InputUi {
  /** Collapse an open Shop sheet to its peek (by transform; cards stay mounted) or restore it. */
  sheet(state: 'peek' | 'restore'): void;
  hoverZone(zone: ZoneId | null): void;
  mode(v: ModeView): void;
  selection(v: SelectionView | null): void;
  /** A drag runs: toasts go 30% and pointer-transparent (M7). */
  dragging(on: boolean): void;
  intent(i: UiIntent): void;
}

export interface GameAccess {
  state(): SimState;
  defs(): SimDefs;
  dispatch(cmd: Command): CmdResult;
}

/** A telemetry row (debug/telemetry.ts; the rows themselves are built by debug/telemetryRows.ts). */
export type { TelemetryRow } from '../debug/telemetry.ts';

/** `__gm.lastDrop()` (M0 contract, grown in M6 with the zone kinds and the result). */
export interface DropRecord {
  k: 'cell' | 'counter' | 'sell' | 'peek' | 'gunSlot' | 'none';
  x?: number;
  y?: number;
  item: string;
  pointerType: string;
  captured: boolean;
  result: string;
  reason: string | null;
}

export interface RouterCfg {
  input: UiData['input'];
  /** Snap-back duration (0 in ?test: zero-duration tweens). */
  snapBackMs: number;
  peekPx: number;
  /** juice.json events.lift.scale (the lifted item's DOM ghost). */
  liftScale: number;
  /** Glow / preview refresh while a drag or selection lasts (the 10 Hz snapshot cadence). */
  refreshMs: number;
}

export interface RouterDeps {
  game: GameAccess;
  layout(): Layout;
  zones: DropZoneRegistry;
  cfg: RouterCfg;
  copy: CopyData;
  money(n: number): string;
  ghost: GhostFx;
  bubble: BubbleFx;
  view: OverlaySink;
  ui: InputUi;
  telemetry: { log(row: TelemetryRow): void };
  sfx(name: 'bonk'): void;
  now(): number;
  onTap?(cell: Cell): void;
  onDrop?(d: DropRecord): void;
}

/** The pointer side the router drives (PointerController). */
export interface GestureHandle {
  adopt(e: PointerEvent): boolean;
  cancel(reason: CancelReason): void;
  kind(): GestureStateKind;
}

// ---- payloads ---------------------------------------------------------------------------------------------

type Payload =
  | { k: 'item'; fam: FamId; tier: number; id: number }
  | { k: 'building'; type: BType; ore: OreId | null; level: number; id: number }
  | { k: 'shop'; item: ShopId; type: BType; ore: OreId | null };

function storedItem(b: Building, id: number): ItemRef | null {
  const lists: (readonly ItemRef[])[] = [];
  if ('slots' in b) for (const l of b.slots) lists.push(l);
  if ('out' in b) lists.push(b.out);
  if (b.type === 'packer') lists.push(b.buf);
  if (b.type === 'gallery') lists.push(b.queue);
  for (const l of lists) for (const it of l) if (it.id === id) return it;
  return null;
}

function payloadOf(s: SimState, d: SimDefs, src: Src): Payload | null {
  switch (src.k) {
    case 'shop': {
      const it = d.shop.find((x) => x.id === src.item);
      if (!it) return null;
      return it.kind === 'deposit'
        ? { k: 'shop', item: src.item, type: 'deposit', ore: it.ref as OreId }
        : { k: 'shop', item: src.item, type: it.ref as BType, ore: null };
    }
    case 'cell': {
      const it = s.floor.find((x) => x.id === src.id);
      if (it) return { k: 'item', fam: it.fam, tier: it.tier, id: it.id };
      const b = s.buildings.find((x) => x.id === src.id);
      if (b)
        return {
          k: 'building',
          type: b.type,
          ore: b.type === 'deposit' ? b.ore : null,
          level: b.level,
          id: b.id,
        };
      return null;
    }
    case 'slot': {
      const b = s.buildings.find((x) => x.id === src.bid);
      const it = b ? storedItem(b, src.id) : null;
      return it ? { k: 'item', fam: it.fam, tier: it.tier, id: it.id } : null;
    }
    case 'tray':
      return null; // v0.2
  }
}

function payloadKey(p: Payload): string {
  switch (p.k) {
    case 'item':
      return `${p.fam}:${p.tier}`;
    case 'building':
      return p.type === 'deposit' ? `deposit.${p.ore}` : p.type;
    case 'shop':
      return p.item;
  }
}

/** A `drag` row's result / reason (M12 vocabulary): the resolver outcome, `snapback`, or `cancel`. */
interface DragRowOutcome {
  result: string;
  reason: string | null;
}

/** A drag's origin for its telemetry row: `cell:x,y` | `shop:<id>` | `slot:<bid>` | `tray` | `none`. */
function srcLabel(src: Src | null): string {
  if (src === null) return 'none';
  switch (src.k) {
    case 'cell':
      return `cell:${src.x},${src.y}`;
    case 'shop':
      return `shop:${src.item}`;
    case 'slot':
      return `slot:${src.bid}`;
    case 'tray':
      return 'tray';
  }
}

/** A drag's final target for its telemetry row: `cell:x,y` | a zone (counter, sell, peek, …) | `none`. */
function targetLabel(t: DropTarget): string {
  if (t.k === 'cell') return `cell:${t.x},${t.y}`;
  return t.k === 'zone' ? t.zone : 'none';
}

function iconOf(p: Payload): IconKey {
  if (p.k === 'item') return { k: 'item', fam: p.fam, tier: p.tier };
  return { k: 'building', type: p.type, ore: p.ore, level: p.k === 'building' ? p.level : 1 };
}

const DIRS_CW: readonly Dir[] = ['N', 'E', 'S', 'W'];

interface DragSession {
  /** What is carried; null = nothing (the press was not on a payload, or the grab failed because the
   * entity moved between press and drag): the gesture runs to its end without a ghost, changing nothing. */
  live: { src: Src; payload: Payload } | null;
  grabbed: boolean;
  shop: boolean;
  originCell: Cell | null;
  origin: { x: number; y: number };
  t0: number;
  x0: number;
  y0: number;
  target: DropTarget;
  /** Hysteresis memory: the last board-cell target. */
  cell: Cell | null;
  outcome: DropOutcome | null;
  hot: { x: number; y: number };
  meta: GestureMeta;
}

interface PaintSession {
  path: PaintPath;
}

// ---- the router -------------------------------------------------------------------------------------------

export class InputRouter {
  private readonly d: RouterDeps;
  private pointer: GestureHandle | null = null;
  private modeState: ModeState = MODE_START;
  private caps: ModeCaps = { coldOpen: false, canBuild: false, canChute: false };
  private lastModeView = '';
  private sel: Selection | null = null;
  private drag: DragSession | null = null;
  private paint: PaintSession | null = null;
  private erase: Cell[] | null = null;
  private pendingAdopt: Src | null = null;
  private readonly rage: RageTapDetector;
  private lastBonk = Number.NEGATIVE_INFINITY;
  private buildOnlyTries = 0;
  private chuteDir: Dir | null = null;
  private targetGlows: InputGlow[] = [];
  private lastRefresh = Number.NEGATIVE_INFINITY;
  private names: Names;
  private namesFor: SimDefs;
  /** Diagnostics for `__gm.input()`. */
  private readonly counts = { bonks: 0, drags: 0, drops: 0, taps: 0, rage: 0, ignored: 0 };
  private readonly intentCounts: Record<string, number> = {};
  private lastIntent: UiIntent | null = null;

  constructor(deps: RouterDeps) {
    this.d = deps;
    this.namesFor = deps.game.defs();
    this.names = namesOf(this.namesFor);
    this.caps = modeCaps(deps.game.state(), this.namesFor);
    this.rage = createRageTapDetector({
      count: deps.cfg.input.rageTapCount,
      windowMs: deps.cfg.input.rageTapWindowMs,
    });
  }

  attach(p: GestureHandle): void {
    this.pointer = p;
  }

  // ---- mode ----------------------------------------------------------------------------------------------

  private s(): SimState {
    return this.d.game.state();
  }

  private defs(): SimDefs {
    const d = this.d.game.defs();
    if (d !== this.namesFor) {
      this.namesFor = d;
      this.names = namesOf(d);
    }
    return d;
  }

  /**
   * Re-reads what the sim allows. Whoever refreshes first sees a mode the sim changed (the cold open
   * starting / ending, Build lost on a reset or load): the gesture is cancelled, the selection cleared and,
   * outside Build, the tool reset — so no caller (a frame, classify, diag, a mode button) can skip that.
   */
  private refreshCaps(): void {
    const before = this.mode();
    this.caps = modeCaps(this.s(), this.defs());
    const after = this.mode();
    if (after === before) return;
    this.pointer?.cancel('mode');
    this.clearSelection();
    if (after !== 'build') this.modeState = MODE_START;
    this.d.zones.refresh(this.d.layout(), this.d.cfg.peekPx);
  }

  mode(): Mode {
    return effectiveMode(this.modeState, this.caps);
  }

  tool(): Tool {
    return effectiveTool(this.modeState, this.caps);
  }

  private get build(): boolean {
    return this.mode() === 'build';
  }

  /** PLAY ⇄ BUILD (Build tab / Done / Esc / key B): any gesture is cancelled and the selection cleared. */
  setBuild(on: boolean): void {
    this.refreshCaps();
    const next = setBuild(this.modeState, on, this.caps);
    if (next === this.modeState) return;
    this.pointer?.cancel('mode');
    this.clearSelection();
    this.modeState = next;
    this.d.zones.refresh(this.d.layout(), this.d.cfg.peekPx);
    this.publishMode(true);
  }

  toggleBuild(): void {
    this.setBuild(this.mode() !== 'build');
  }

  setTool(tool: Tool): void {
    this.refreshCaps();
    const next = setTool(this.modeState, tool, this.caps);
    if (next === this.modeState) return;
    this.pointer?.cancel('mode');
    this.clearSelection();
    this.modeState = next;
    this.publishMode(true);
  }

  private publishMode(force = false): void {
    const v: ModeView = {
      mode: this.mode(),
      tool: this.tool(),
      canBuild: this.caps.canBuild && !this.caps.coldOpen,
      canChute: this.caps.canChute,
      undo: this.build ? this.s().transient.undo.length : 0,
    };
    const key = `${v.mode}|${v.tool}|${v.canBuild}|${v.canChute}|${v.undo}`;
    if (!force && key === this.lastModeView) return;
    this.lastModeView = key;
    this.d.ui.mode(v);
  }

  /** Per display frame: mode caps, the mode view, and the 10 Hz glow / preview refresh. */
  frame(nowMs: number): void {
    this.refreshCaps();
    this.publishMode();
    if (this.sel !== null && this.sel.k === 'item' && !this.selectionAlive()) this.clearSelection();
    if (
      (this.drag !== null || this.sel !== null) &&
      nowMs - this.lastRefresh >= this.d.cfg.refreshMs
    ) {
      this.lastRefresh = nowMs;
      const live = this.drag?.live ?? null;
      if (this.drag !== null && live !== null) {
        this.targetGlows = this.glowsFor(live.src);
        this.hover(this.drag, true);
      } else if (this.sel !== null) {
        this.targetGlows = this.glowsFor(this.selSrc(this.sel));
        this.publishSelection();
      }
      this.pushOverlay();
    }
  }

  /** New layout (resize / expansion): zone rects are re-read. */
  relayout(): void {
    this.d.zones.refresh(this.d.layout(), this.d.cfg.peekPx);
  }

  // ---- the §2.7 IDLE --down--> table -----------------------------------------------------------------------

  /** What a pointerdown at (x, y) starts, for the current mode and tool. */
  classify(x: number, y: number, _mouse: boolean): DownKind {
    this.refreshCaps();
    const s = this.s();
    const h = hitTest(s, this.defs(), this.d.layout(), x, y);
    const mode = this.mode();
    const obj = objKey(h);
    const press: DownKind = {
      k: 'press',
      obj,
      draggable: isDraggable(h, mode),
      longPress: hasLongPress(h, mode),
    };
    if (mode === 'coldOpen') {
      if (h.k === 'building' && h.b.type === 'gallery') return { k: 'tapOnly', obj };
      return { ...press, draggable: false, longPress: false };
    }
    // a Shop tap-placement is waiting for a target cell: every tap resolves through it
    if (this.sel !== null && this.sel.k === 'shop')
      return { ...press, draggable: false, longPress: false };
    if (mode === 'play' && h.k === 'building' && h.b.type === 'deposit' && h.b.ghost === null)
      return { k: 'deposit', cell: { x: h.x, y: h.y } };
    if (mode === 'build') {
      const tool = this.tool();
      if (tool === 'chute' && (h.k === 'empty' || h.k === 'building'))
        return { k: 'paint', cell: { x: h.x, y: h.y } };
      if (tool === 'sell' && h.k === 'building' && h.b.type === 'chute')
        return { k: 'erase', cell: { x: h.x, y: h.y } };
    }
    return press;
  }

  // ---- gestures -----------------------------------------------------------------------------------------

  gesture(outs: readonly GestureOut[], meta: GestureMeta): void {
    for (const o of outs) this.one(o, meta);
  }

  private one(o: GestureOut, meta: GestureMeta): void {
    switch (o.k) {
      case 'hit': {
        this.d.onTap?.(o.cell);
        this.d.view.setHighlight(o.cell);
        const r = this.d.game.dispatch({ c: 'hit', x: o.cell.x, y: o.cell.y });
        // a `cooldown` refusal is fast mining meeting the shared tap cooldown (silent, L9): not rage
        this.noteTap(o.cell, r.ok || r.reason === 'cooldown');
        return;
      }
      case 'hold':
        this.d.game.dispatch({ c: 'hold', x: o.cell.x, y: o.cell.y });
        return;
      case 'holdEnd':
        this.d.game.dispatch({ c: 'holdEnd' });
        return;
      case 'tap':
        this.tap(o.x, o.y, o.double);
        return;
      case 'longPress': {
        const h = hitTest(this.s(), this.defs(), this.d.layout(), o.x, o.y);
        this.intent({ k: 'info', target: h });
        return;
      }
      case 'fire':
        this.fire(o.x, o.y);
        return;
      case 'dragStart':
        this.startDrag(o.x, o.y, o.x0, o.y0, o.adopted, meta);
        return;
      case 'dragMove':
        if (this.drag) this.moveDrag(this.drag, o.x, o.y);
        return;
      case 'drop':
        this.dropDrag(o.x, o.y, meta);
        return;
      case 'dragCancel':
        this.cancelDrag(o.reason);
        return;
      case 'noDrag':
        this.noDrag(o.x, o.y, o.obj);
        return;
      case 'paintStart':
        this.clearSelection();
        this.paint = { path: startPath(o.cell) };
        this.pushOverlay();
        return;
      case 'paintMove':
        this.paintMove(o.cell, o.x, o.y);
        return;
      case 'paintEnd':
        this.paintEnd();
        return;
      case 'paintCancel':
        this.paint = null;
        this.pushOverlay();
        return;
      case 'eraseStart':
        this.clearSelection();
        this.erase = [o.cell];
        this.pushOverlay();
        return;
      case 'eraseMove':
        this.eraseMove(o.cell);
        return;
      case 'eraseEnd':
        this.eraseEnd();
        return;
      case 'eraseCancel':
        this.erase = null;
        this.pushOverlay();
        return;
      case 'ignored':
        this.counts.ignored += 1;
        return;
    }
  }

  private intent(i: UiIntent): void {
    this.intentCounts[i.k] = (this.intentCounts[i.k] ?? 0) + 1;
    this.lastIntent = i;
    this.d.ui.intent(i);
  }

  /** Every board tap feeds the rage-tap detector (M12, debug/rageTap.ts): one `rage_tap` row per streak. */
  private noteTap(cell: Cell | null, effect: boolean): void {
    if (cell === null) return;
    const r = this.rage.tap({ ms: this.d.now(), x: cell.x, y: cell.y, effect });
    if (r === null) return;
    this.counts.rage += 1;
    this.d.telemetry.log(rageTapRow(r, { mode: this.mode(), tool: this.tool() }));
  }

  private fire(x: number, y: number): void {
    const s = this.s();
    const h = hitTest(s, this.defs(), this.d.layout(), x, y);
    const cell = hitCell(h);
    if (cell) {
      this.d.onTap?.(cell);
      this.d.view.setHighlight(cell);
    }
    if (h.k !== 'building' || h.b.type !== 'gallery') return;
    const r = this.d.game.dispatch({ c: 'tapGallery', bid: h.b.id });
    this.noteTap(cell, r.ok);
  }

  // ---- taps ---------------------------------------------------------------------------------------------

  private tapTarget(x: number, y: number, h: HitObj): TapTarget {
    const zone = this.d.zones.hit(x, y, { mode: this.mode(), shop: this.sel?.k === 'shop' });
    if (zone !== null) return { k: 'zone', zone };
    switch (h.k) {
      case 'item':
        return { k: 'item', id: h.id, x: h.x, y: h.y };
      case 'building':
        return { k: 'building', id: h.id, x: h.x, y: h.y };
      case 'empty':
        return { k: 'empty', x: h.x, y: h.y };
      case 'rock':
        return { k: 'rock', x: h.x, y: h.y };
      case 'none':
        return { k: 'none' };
    }
  }

  private tap(x: number, y: number, double: boolean): void {
    this.counts.taps += 1;
    const s = this.s();
    const d = this.defs();
    const h = hitTest(s, d, this.d.layout(), x, y);
    const cell = hitCell(h);
    if (cell) {
      this.d.onTap?.(cell);
      this.d.view.setHighlight(cell);
    }
    const mode = this.mode();
    if (mode === 'coldOpen') {
      // §2.7 "Cold open": only the Gallery fires; any other tap wiggles it
      this.intent({ k: 'nudge' });
      this.noteTap(cell, false);
      return;
    }
    const target = this.tapTarget(x, y, h);
    if (this.sel === null && mode === 'build' && this.tool() === 'sell' && target.k !== 'zone') {
      // the Sell tool never selects: a tap sells what it lands on (a building, a chute, a loose item)
      this.noteTap(cell, this.tapSemantics(h, mode, x, y));
      return;
    }
    const act = selectionTap(this.sel, target, {
      t: this.d.now(),
      double,
      quickMergeOn: hasFeature(s, d, 'quickMerge'),
      doubleTapMs: this.d.cfg.input.doubleTapMs,
      preview: (from, to) => resolveDrop(s, d, from, to, this.build),
    });
    const at = this.targetPoint(target, x, y);
    switch (act.k) {
      case 'select':
        this.select(act.sel);
        this.noteTap(cell, true);
        return;
      case 'clear':
        this.clearSelection(); // a deselect is an effect (selectionTap clears only an existing selection)
        this.noteTap(cell, true);
        return;
      case 'keep':
        this.noteTap(cell, false);
        return;
      case 'quickMerge': {
        const r = this.d.game.dispatch({ c: 'quickMerge', id: act.id });
        if (!r.ok) this.refuse(r.reason, at.x, at.y, null);
        this.clearSelection();
        this.noteTap(cell, r.ok);
        return;
      }
      case 'refuse':
        this.refuse(act.reason, at.x, at.y, target.k === 'building' ? target.id : null);
        this.noteTap(cell, false);
        return;
      case 'drop': {
        const out = resolveDrop(s, d, act.from, act.to, this.build);
        if (act.from.k === 'shop' && out.k !== 'buyMerge' && this.mergeOnly(act.from)) {
          this.intent({ k: 'status', text: this.mergeOnlyText(act.from.item) });
          this.noteTap(cell, false);
          return;
        }
        const r = this.d.game.dispatch({
          c: 'drop',
          from: act.from,
          to: act.to,
          build: this.build,
        });
        if (r.ok) {
          this.afterDrop(out);
          this.clearSelection();
        } else {
          this.refuse(r.reason, at.x, at.y, target.k === 'building' ? target.id : null);
        }
        this.noteTap(cell, r.ok);
        return;
      }
      case 'pass':
        // a DOM zone (the counter) handles its own taps in the UI shell; board objects have tap semantics
        this.noteTap(cell, target.k === 'zone' ? false : this.tapSemantics(h, mode, x, y));
        return;
    }
  }

  /** Client point of a tap target (reason bubble position). */
  private targetPoint(t: TapTarget, x: number, y: number): { x: number; y: number } {
    if (t.k === 'item' || t.k === 'building' || t.k === 'empty' || t.k === 'rock')
      return cellCenter(this.d.layout(), t.x, t.y);
    return { x, y };
  }

  /** The object's own tap semantics (§2.7 "Tap semantics"); true when something happened. */
  private tapSemantics(h: HitObj, mode: Mode, x: number, y: number): boolean {
    if (mode === 'build' && this.tool() === 'sell') {
      if (h.k === 'building') {
        const r =
          h.b.type === 'chute'
            ? this.d.game.dispatch({ c: 'eraseChutes', cells: [{ x: h.x, y: h.y }] })
            : h.b.ghost !== null
              ? this.d.game.dispatch({ c: 'cancelGhost', bid: h.id })
              : this.d.game.dispatch({ c: 'sellBuilding', bid: h.id });
        if (!r.ok) this.refuse(r.reason, x, y, h.id);
        return r.ok;
      }
      if (h.k === 'item') {
        const from: Src = { k: 'cell', x: h.x, y: h.y, id: h.id };
        const to: Dst = { k: 'sell' };
        const out = resolveDrop(this.s(), this.defs(), from, to, true);
        const r = this.d.game.dispatch({ c: 'drop', from, to, build: true });
        if (r.ok) this.afterDrop(out);
        else this.refuse(r.reason, x, y, null);
        return r.ok;
      }
      return false;
    }
    if (h.k === 'building' || h.k === 'rock') {
      this.intent({ k: 'card', target: h, mode });
      return true;
    }
    return false;
  }

  /** Counter / Sell-tool tap from the UI (M7 counter) while something is selected. */
  tapZone(zone: ZoneId): void {
    if (this.sel === null) return;
    const s = this.s();
    const d = this.defs();
    const act = selectionTap(
      this.sel,
      { k: 'zone', zone },
      {
        t: this.d.now(),
        double: false,
        quickMergeOn: false,
        doubleTapMs: this.d.cfg.input.doubleTapMs,
        preview: (from, to) => resolveDrop(s, d, from, to, this.build),
      },
    );
    // a refusal is shown like on the board: the reason bubble above the zone + the rate-limited bonk
    const rc = this.d.zones.rects()[zone];
    const at = rc ? { x: rc.x + rc.w / 2, y: rc.y } : { x: 0, y: 0 };
    if (act.k === 'drop') {
      const out = resolveDrop(s, d, act.from, act.to, this.build);
      const r = this.d.game.dispatch({ c: 'drop', from: act.from, to: act.to, build: this.build });
      if (r.ok) {
        this.afterDrop(out);
        this.clearSelection();
      } else this.refuse(r.reason, at.x, at.y, null);
    } else if (act.k === 'refuse') this.refuse(act.reason, at.x, at.y, null);
    else if (act.k === 'clear') this.clearSelection();
  }

  // ---- selection ------------------------------------------------------------------------------------------

  private selSrc(sel: Selection): Src {
    return sel.k === 'item' ? sel.src : { k: 'shop', item: sel.item };
  }

  private selectionAlive(): boolean {
    const sel = this.sel;
    if (sel === null || sel.k !== 'item') return true;
    const it = this.s().floor.find((x) => x.id === sel.src.id);
    return it !== undefined && it.x === sel.src.x && it.y === sel.src.y;
  }

  private select(sel: Selection): void {
    this.sel = sel;
    this.targetGlows = this.glowsFor(this.selSrc(sel));
    this.lastRefresh = this.d.now();
    this.publishSelection();
    this.pushOverlay();
  }

  private publishSelection(): void {
    const sel = this.sel;
    if (sel === null) {
      this.d.ui.selection(null);
      return;
    }
    let sellValue: number | null = null;
    if (sel.k === 'item') {
      const out = resolveDrop(this.s(), this.defs(), sel.src, { k: 'counter' }, false);
      sellValue = out.k === 'sell' ? out.value : null;
    }
    this.d.ui.selection({
      k: sel.k,
      id: sel.k === 'item' ? sel.src.id : null,
      item: sel.k === 'shop' ? sel.item : null,
      sellValue,
    });
  }

  clearSelection(): void {
    if (this.sel === null) return;
    this.sel = null;
    this.targetGlows = [];
    this.d.ui.selection(null);
    this.pushOverlay();
  }

  /** §2.8 step 7: tap a Shop card, then Place → valid cells glow; tapping a cell buys. */
  beginTapPlace(item: ShopId): void {
    this.refreshCaps();
    if (this.mode() === 'coldOpen') return;
    this.select({ k: 'shop', item, t: this.d.now() });
    this.intent({
      k: 'status',
      text: this.mergeOnly({ k: 'shop', item })
        ? this.mergeOnlyText(item)
        : dragText(this.d.copy, 'tapPlace', { name: this.names.shop(item) }),
    });
  }

  // ---- drag ---------------------------------------------------------------------------------------------

  /** §2.8 shop handoff: a DOM card hands its pointer over with a payload (no grab for a shop card). */
  adopt(e: PointerEvent, src: Src): boolean {
    // any mode: in the cold open the Shop is not shown yet (M7), and the resolver refuses a drop anyway
    if (this.pointer === null) return false;
    this.pendingAdopt = src;
    const ok = this.pointer.adopt(e);
    if (!ok) this.pendingAdopt = null;
    return ok;
  }

  dragging(): boolean {
    return this.drag !== null;
  }

  private startDrag(
    x: number,
    y: number,
    x0: number,
    y0: number,
    adopted: boolean,
    meta: GestureMeta,
  ): void {
    const s = this.s();
    const d = this.defs();
    let src: Src | null = null;
    if (adopted) {
      src = this.pendingAdopt;
      this.pendingAdopt = null;
    } else {
      const h = hitTest(s, d, this.d.layout(), x0, y0);
      if (h.k === 'item' || (h.k === 'building' && this.build))
        src = { k: 'cell', x: h.x, y: h.y, id: h.id };
    }
    const payload = src === null ? null : payloadOf(s, d, src);
    const layout = this.d.layout();
    this.clearSelection();
    this.d.zones.refresh(layout, this.d.cfg.peekPx);
    let originCell: Cell | null = null;
    if (src?.k === 'cell') originCell = { x: src.x, y: src.y };
    if (src?.k === 'slot') {
      const b = s.buildings.find((q) => q.id === src.bid);
      if (b) originCell = { x: b.x, y: b.y };
    }
    const peek = this.d.zones.rects().peek;
    const origin = originCell
      ? cellCenter(layout, originCell.x, originCell.y)
      : peek
        ? { x: x0, y: peek.y + peek.h / 2 }
        : { x: x0, y: y0 };
    let live: DragSession['live'] = src !== null && payload !== null ? { src, payload } : null;
    let grabbed = false;
    if (live !== null && (live.src.k === 'cell' || live.src.k === 'slot')) {
      grabbed = this.d.game.dispatch({ c: 'grab', id: live.src.id }).ok;
      if (!grabbed) live = null; // the entity moved between press and drag
    }
    const sess: DragSession = {
      live,
      grabbed,
      shop: src?.k === 'shop',
      originCell,
      origin,
      t0: this.d.now(),
      x0,
      y0,
      target: { k: 'none' },
      cell: null,
      outcome: null,
      hot: { x, y: y - this.d.cfg.input.liftPx },
      meta,
    };
    this.drag = sess;
    this.counts.drags += 1;
    this.d.ui.sheet('peek');
    this.d.ui.dragging(true);
    if (live !== null) {
      this.d.ghost.show(iconOf(live.payload), layout.cellCss * this.d.cfg.liftScale);
      this.targetGlows = this.glowsFor(live.src);
      this.lastRefresh = this.d.now();
    }
    this.moveDrag(sess, x, y);
    this.pushOverlay();
  }

  private moveDrag(sess: DragSession, x: number, y: number): void {
    const hot = { x, y: y - this.d.cfg.input.liftPx };
    sess.hot = hot;
    if (sess.live === null) return;
    this.d.ghost.moveTo(hot.x, hot.y);
    const zone = this.d.zones.hit(hot.x, hot.y, { mode: this.mode(), shop: sess.shop });
    let target: DropTarget;
    if (zone !== null) {
      target = { k: 'zone', zone };
      sess.cell = null;
    } else {
      const layout = this.d.layout();
      const cell = stickyCell(layout, sess.cell, hot.x, hot.y, this.d.cfg.input.hysteresisPct);
      sess.cell = cell;
      target = cell ? { k: 'cell', x: cell.x, y: cell.y } : { k: 'none' };
    }
    const changed = !sameTarget(target, sess.target);
    sess.target = target;
    this.hover(sess, changed);
  }

  private dstOf(t: DropTarget): Dst | null {
    if (t.k === 'cell') return { k: 'cell', x: t.x, y: t.y };
    if (t.k === 'zone' && t.zone === 'counter') return { k: 'counter' };
    if (t.k === 'zone' && t.zone === 'sell') return { k: 'sell' };
    return null; // the peek cancels a shop drag; the gun slot is v0.2
  }

  /** Hover feedback for the current target: highlight, zone, preview bubble. */
  private hover(sess: DragSession, recompute: boolean): void {
    const live = sess.live;
    if (live === null) return;
    const t = sess.target;
    if (recompute) {
      const dst = this.dstOf(t);
      sess.outcome =
        dst === null ? null : resolveDrop(this.s(), this.defs(), live.src, dst, this.build);
      this.d.view.setHighlight(t.k === 'cell' ? { x: t.x, y: t.y } : null);
      this.d.ui.hoverZone(t.k === 'zone' ? t.zone : null);
    }
    const out = sess.outcome;
    const size = this.d.layout().cellCss * this.d.cfg.liftScale;
    const bx = sess.hot.x;
    const by = sess.hot.y - size / 2 - this.d.cfg.input.bubbleGapPx;
    const p =
      out === null
        ? null
        : previewText(out, this.textCtx(), itemOf(live.payload), this.buildingAt(t));
    this.d.bubble.preview(p ? p.text : null, p ? p.bad : false, bx, by, p?.icon ?? null);
  }

  private buildingAt(t: DropTarget): number | null {
    if (t.k !== 'cell') return null;
    const v = cellView(this.s(), this.defs(), t.x, t.y);
    return v.k === 'building' ? v.b.id : null;
  }

  private textCtx(): TextCtx {
    const s = this.s();
    const d = this.defs();
    return {
      copy: this.d.copy,
      names: this.names,
      money: this.d.money,
      s,
      accepts: (bid) => handAccepts(s, d, bid),
    };
  }

  private dropDrag(x: number, y: number, meta: GestureMeta): void {
    const sess = this.drag;
    if (sess === null) return;
    this.moveDrag(sess, x, y);
    const t = sess.target;
    const kind: DropRecord['k'] = t.k === 'cell' ? 'cell' : t.k === 'zone' ? t.zone : 'none';
    const live = sess.live;
    const dst = this.dstOf(t);
    let result: string;
    let reason: RejectReason | null = null;
    /** The telemetry `drag` row's outcome (M12 vocabulary; `lastDrop` keeps the M6 one). */
    let row: DragRowOutcome;
    if (live === null) {
      result = 'stale';
      row = { result: 'cancel', reason: 'stale' }; // nothing was carried: not a mis-drop
      if (sess.grabbed) this.d.game.dispatch({ c: 'release' });
      this.d.ghost.hide();
    } else if (dst === null) {
      // outside everything, the peek (cancel a shop drag) or an inactive zone: silent snap back
      result = kind === 'peek' ? 'cancel' : 'none';
      row =
        kind === 'peek'
          ? { result: 'cancel', reason: 'peek' }
          : { result: 'snapback', reason: kind === 'none' ? 'offBoard' : kind };
      if (sess.grabbed) this.d.game.dispatch({ c: 'release' });
      this.snapBack(sess, null);
    } else {
      const out = resolveDrop(this.s(), this.defs(), live.src, dst, this.build);
      const r = this.d.game.dispatch({ c: 'drop', from: live.src, to: dst, build: this.build });
      this.counts.drops += 1;
      if (r.ok) {
        result = out.k;
        row = { result, reason: null };
        this.d.ghost.hide();
        this.afterDrop(out);
      } else {
        result = 'reject';
        reason = r.reason;
        row = { result: 'snapback', reason };
        this.snapBack(sess, r.reason);
      }
    }
    this.d.onDrop?.({
      k: kind,
      ...(t.k === 'cell' ? { x: t.x, y: t.y } : {}),
      item: live === null ? 'none' : payloadKey(live.payload),
      pointerType: meta.pointerType,
      captured: meta.captured,
      result,
      reason,
    });
    // a committed drop onto a cell leaves the tap highlight there (the "cursor"); anything else clears it
    const ok = live !== null && dst !== null && reason === null;
    this.endDrag(sess, x, y, row, ok && t.k === 'cell' ? { x: t.x, y: t.y } : null);
  }

  private cancelDrag(why: CancelReason): void {
    const sess = this.drag;
    if (sess === null) return;
    if (sess.grabbed) this.d.game.dispatch({ c: 'release' });
    this.snapBack(sess, null);
    const fingerY = sess.hot.y + this.d.cfg.input.liftPx;
    this.endDrag(sess, sess.hot.x, fingerY, { result: 'cancel', reason: why }, null);
  }

  private endDrag(
    sess: DragSession,
    x: number,
    y: number,
    row: DragRowOutcome,
    highlight: Cell | null,
  ): void {
    this.drag = null;
    this.targetGlows = [];
    this.d.bubble.preview(null, false, 0, 0);
    this.d.ui.hoverZone(null);
    this.d.ui.dragging(false);
    this.d.ui.sheet('restore');
    this.d.view.setHighlight(highlight);
    this.pushOverlay();
    // the one `drag` row of this drag (M12 decision 4 format: subject payload, value ms, extra {from, to,
    // result, reason, distPx}; distPx = the finger's straight distance from the press)
    this.d.telemetry.log(
      dragRow({
        payload: sess.live === null ? 'none' : payloadKey(sess.live.payload),
        from: srcLabel(sess.live?.src ?? null),
        to: targetLabel(sess.target),
        result: row.result,
        reason: row.reason,
        ms: Math.max(0, this.d.now() - sess.t0),
        distPx: Math.hypot(x - sess.x0, y - sess.y0),
      }),
    );
  }

  /** 180 ms Back.out to the origin; a reason bubble (1.5 s) and one ui:bonk per bonkCooldownMs. */
  private snapBack(sess: DragSession, reason: RejectReason | null): void {
    this.d.ghost.snapBack(sess.origin.x, sess.origin.y, this.d.cfg.snapBackMs);
    if (reason !== null) this.refuse(reason, sess.hot.x, sess.hot.y, this.buildingAt(sess.target));
  }

  /** Reason bubble + rate-limited bonk. `cooldown` refusals are silent (M4b: the shared tap cooldown). */
  private refuse(reason: RejectReason, x: number, y: number, bid: number | null): void {
    if (reason === 'cooldown') return;
    const cfg = this.d.cfg.input;
    this.d.bubble.reason(reasonText(reason, this.textCtx(), bid), x, y, cfg.reasonBubbleMs);
    const now = this.d.now();
    if (now - this.lastBonk >= cfg.bonkCooldownMs) {
      this.lastBonk = now;
      this.counts.bonks += 1;
      this.d.sfx('bonk');
    }
  }

  /** After a successful drop: the sale's undo toast, the new building's ⟳ handle. */
  private afterDrop(out: DropOutcome): void {
    const s = this.s();
    const d = this.defs();
    if (out.k === 'sell') {
      const u = canUndoSell(s, d);
      if (u.ok) this.intent({ k: 'undoToast', token: u.token, value: u.value });
    }
    if (out.k === 'buyPlace') {
      const b = s.buildings.find((q) => q.x === out.x && q.y === out.y);
      if (b) this.intent({ k: 'rotateHandle', bid: b.id, ms: this.d.cfg.input.rotateHandleMs });
    }
  }

  private noDrag(x: number, y: number, obj: string): void {
    if (!obj.startsWith('bld:') || this.mode() !== 'play') return;
    this.buildOnlyTries += 1;
    if (this.buildOnlyTries < this.d.cfg.input.buildOnlyHintAfterTries) return;
    this.buildOnlyTries = 0;
    this.refuse('buildOnly', x, y, null);
    this.intent({ k: 'hint', text: dragText(this.d.copy, 'buildOnlyHint', {}) });
  }

  // ---- paint / erase ------------------------------------------------------------------------------------

  private classifyCell = (c: Cell): CellClass => {
    const v = cellView(this.s(), this.defs(), c.x, c.y);
    switch (v.k) {
      case 'rock':
      case 'item':
        return 'blocked';
      case 'empty':
        return 'open';
      case 'building':
        return v.b.type === 'chute' ? 'chute' : 'building';
    }
  };

  private paintMove(cell: Cell | null, x: number, y: number): void {
    const p = this.paint;
    if (p === null || cell === null) return;
    const last = p.path.cells[p.path.cells.length - 1];
    if (last === undefined || (last.x === cell.x && last.y === cell.y)) return;
    const c = cellCenter(this.d.layout(), last.x, last.y);
    const dominantX = Math.abs(x - c.x) >= Math.abs(y - c.y);
    const next = extendPath(p.path, cell, dominantX, this.classifyCell);
    if (next === p.path) return;
    p.path = next;
    this.pushOverlay();
  }

  private paintEnd(): void {
    const p = this.paint;
    this.paint = null;
    this.pushOverlay();
    if (p === null) return;
    const cells = p.path.cells;
    const first = cells[0];
    if (first === undefined) return;
    const layout = this.d.layout();
    const at = cellCenter(layout, first.x, first.y);
    if (cells.length === 1) {
      // UX §3.3 chute tap: an empty cell gets one chute in the last-used direction; a chute turns 90° cw
      const cls = this.classifyCell(first);
      if (cls === 'chute') {
        const v = cellView(this.s(), this.defs(), first.x, first.y);
        if (v.k === 'building') this.d.game.dispatch({ c: 'rotate', bid: v.b.id });
        return;
      }
      if (cls === 'building') {
        // a plain tap on a machine with the Chute tool: its card, like the Move tool
        const h = hitTest(this.s(), this.defs(), layout, at.x, at.y);
        this.intent({ k: 'card', target: h, mode: this.mode() });
        return;
      }
      if (cls !== 'open') return;
      const r = this.d.game.dispatch({ c: 'paintChutes', path: [first] });
      if (!r.ok) {
        this.refuse(r.reason, at.x, at.y, null);
        return;
      }
      this.turnTo(first, this.chuteDir);
      return;
    }
    const r = this.d.game.dispatch({
      c: 'paintChutes',
      path: cells.map((c) => ({ x: c.x, y: c.y })),
    });
    if (!r.ok) {
      const end = cells[cells.length - 1] ?? first;
      const e = cellCenter(layout, end.x, end.y);
      this.refuse(r.reason, e.x, e.y, null);
      return;
    }
    const dir = lastDir(p.path);
    if (dir !== null) this.chuteDir = dir;
  }

  /** Rotates the chute on `cell` clockwise until it faces `dir` (rotate is free, R14). */
  private turnTo(cell: Cell, dir: Dir | null): void {
    if (dir === null) return;
    for (let i = 0; i < DIRS_CW.length; i++) {
      const v = cellView(this.s(), this.defs(), cell.x, cell.y);
      if (v.k !== 'building' || v.b.type !== 'chute' || v.b.dir === dir) return;
      this.d.game.dispatch({ c: 'rotate', bid: v.b.id });
    }
  }

  private eraseMove(cell: Cell | null): void {
    const e = this.erase;
    if (e === null || cell === null) return;
    if (e.some((c) => c.x === cell.x && c.y === cell.y)) return;
    if (this.classifyCell(cell) !== 'chute') return;
    e.push({ x: cell.x, y: cell.y });
    this.pushOverlay();
  }

  private eraseEnd(): void {
    const e = this.erase;
    this.erase = null;
    this.pushOverlay();
    if (e === null || e.length === 0) return;
    const r = this.d.game.dispatch({ c: 'eraseChutes', cells: e });
    const first = e[0];
    if (!r.ok && first) {
      const c = cellCenter(this.d.layout(), first.x, first.y);
      this.refuse(r.reason, c.x, c.y, null);
    }
  }

  // ---- overlay ------------------------------------------------------------------------------------------

  /**
   * Tutorial focus (v0.1 playtest fix): while the dropOnVein tip is active and a Shop card can merge onto a
   * vein it owns, that card only merges — the glows show the vein alone and a tap-place on a free cell is
   * refused with "Tap your Lead Vein to merge them" (a second L1 vein would stall the u04 goal).
   */
  private mergeOnly(src: Src): boolean {
    if (src.k !== 'shop') return false;
    const s = this.s();
    const d = this.defs();
    if (tutorialView(s, d).tip?.id !== 'dropOnVein') return false;
    return dropTargets(s, d, src, this.build).some((c) => c.out.k === 'buyMerge');
  }

  private mergeOnlyText(item: ShopId): string {
    return dragText(this.d.copy, 'tapMergeOnly', { name: this.names.shop(item) });
  }

  private glowsFor(src: Src): InputGlow[] {
    const out: InputGlow[] = [];
    const only = this.mergeOnly(src);
    for (const c of dropTargets(this.s(), this.defs(), src, this.build)) {
      if (only && c.out.k !== 'buyMerge') continue;
      const k = glowOf(c.out);
      if (k !== null) out.push({ x: c.x, y: c.y, kind: k });
    }
    return out;
  }

  private paintGlows(): InputGlow[] {
    const out: InputGlow[] = [];
    if (this.erase !== null) {
      for (const c of this.erase) out.push({ x: c.x, y: c.y, kind: 'pathBad' });
      return out;
    }
    const p = this.paint;
    if (p === null) return out;
    const prev = paintPreview(this.s(), this.defs(), p.path.cells);
    const placed = prev.ok ? prev.cells : [];
    const sinkEnd = endsOnSink(p.path, this.classifyCell);
    p.path.cells.forEach((c, i) => {
      const isPlaced = placed.some((q) => q.x === c.x && q.y === c.y);
      const endBuilding =
        (i === 0 && this.classifyCell(c) === 'building') ||
        (i === p.path.cells.length - 1 && sinkEnd);
      if (endBuilding) return;
      out.push({ x: c.x, y: c.y, kind: isPlaced ? 'path' : 'pathBad' });
    });
    if (p.path.blocked !== null)
      out.push({ x: p.path.blocked.x, y: p.path.blocked.y, kind: 'pathBad' });
    return out;
  }

  private pushOverlay(): void {
    const glows: InputGlow[] = [...this.targetGlows];
    let hidden: number | null = null;
    let lifted: InputOverlay['lifted'] = null;
    const sess = this.drag;
    if (sess !== null && sess.live !== null) {
      if (sess.originCell) glows.push({ ...sess.originCell, kind: 'home' });
      if (sess.live.src.k === 'cell') hidden = sess.live.src.id;
    }
    const sel = this.sel;
    if (sel !== null && sel.k === 'item') {
      glows.push({ x: sel.src.x, y: sel.src.y, kind: 'select' });
      lifted = { id: sel.src.id, cells: this.d.cfg.input.selectLiftPx / this.d.layout().cellCss };
    }
    for (const g of this.paintGlows()) glows.push(g);
    this.d.view.setInputOverlay({ glows, hidden, lifted });
  }

  // ---- keys ---------------------------------------------------------------------------------------------

  key(a: KeyAction): void {
    switch (a) {
      case 'toggleBuild':
        this.toggleBuild();
        return;
      case 'toggleShop':
        if (this.mode() !== 'coldOpen') this.intent({ k: 'shop', open: 'toggle' });
        return;
      case 'mergeAll':
        this.d.game.dispatch({ c: 'mergeAll' });
        return;
      case 'undo': {
        const s = this.s();
        const d = this.defs();
        if (this.build) {
          this.d.game.dispatch({ c: 'undoBuild' });
          return;
        }
        const u = canUndoSell(s, d);
        if (u.ok) this.d.game.dispatch({ c: 'undoSell', token: u.token });
        return;
      }
      case 'escape': {
        const k = this.pointer?.kind() ?? 'idle';
        if (k !== 'idle') this.pointer?.cancel('mode');
        else if (this.sel !== null) this.clearSelection();
        else if (this.build) this.setBuild(false);
        else this.intent({ k: 'shop', open: false });
        return;
      }
    }
  }

  // ---- diagnostics --------------------------------------------------------------------------------------

  diag(): InputDiag {
    this.refreshCaps(); // the mode follows the sim even between display frames (tests apply commands directly)
    const sess = this.drag;
    return {
      mode: this.mode(),
      tool: this.tool(),
      gesture: this.pointer?.kind() ?? 'idle',
      selection:
        this.sel === null
          ? null
          : this.sel.k === 'item'
            ? { k: 'item', id: this.sel.src.id }
            : { k: 'shop', item: this.sel.item },
      drag:
        sess === null
          ? null
          : {
              payload: sess.live === null ? 'none' : payloadKey(sess.live.payload),
              target: sess.target,
              outcome: sess.outcome?.k ?? null,
            },
      glows: this.targetGlows.map((g) => ({ ...g })),
      paint: this.paint ? this.paint.path.cells.map((c) => ({ ...c })) : null,
      counts: { ...this.counts },
      intents: { ...this.intentCounts },
      lastIntent: this.lastIntent,
    };
  }
}

export interface InputDiag {
  mode: Mode;
  tool: Tool;
  gesture: GestureStateKind;
  selection: { k: 'item'; id: number } | { k: 'shop'; item: ShopId } | null;
  drag: { payload: string; target: DropTarget; outcome: string | null } | null;
  glows: InputGlow[];
  paint: Cell[] | null;
  counts: {
    bonks: number;
    drags: number;
    drops: number;
    taps: number;
    rage: number;
    ignored: number;
  };
  /** How many intents of each kind were emitted. */
  intents: Record<string, number>;
  lastIntent: UiIntent | null;
}

function sameTarget(a: DropTarget, b: DropTarget): boolean {
  if (a.k !== b.k) return false;
  if (a.k === 'cell' && b.k === 'cell') return a.x === b.x && a.y === b.y;
  if (a.k === 'zone' && b.k === 'zone') return a.zone === b.zone;
  return true;
}

function itemOf(p: Payload): { fam: FamId; tier: number } | null {
  return p.k === 'item' ? { fam: p.fam, tier: p.tier } : null;
}

function dragText(copy: CopyData, key: string, vars: Record<string, string | number>): string {
  return fillCopy(copy.drag[key] ?? `[drag.${key}]`, vars);
}
